
import React, { useState, useEffect, useCallback } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  Attributes,
  Condition,
  Creature,
  CreatureGift,
  SENSES,
  Skill,
  SKILLS,
  Tool,
} from "@dndModels";
import { 
  CHARACTER_LEVELS,
  CR_OPTIONS, 
  CREATURE_CRS,
  DAMAGE_TYPES,
  PROFICIENCY_OPTIONS
} from "@dndModels/Constants";
import { AbilityScoreType, DamageType } from "@dndModels/Types";

import DandDAPI from "@dndUtilities/system_api";
import { makeStyles } from "@utilities/theme_utilities";

import SelectStringBox from "@components/input/selects/SelectStringBox";
import StringBox from "@components/input/strings/StringBox";
import CenteredMenu from "@components/input/menus/CenteredMenu";
import DeleteButton from "@components/input/buttons/DeleteButton";
import CopyButton from "@components/input/buttons/CopyButton";
import AddButton from "@components/input/buttons/AddButton";
import CheckBox from "@components/input/CheckBox";
import ButtonBox from "@components/input/buttons/ButtonBox";
import ToggleButtonBox from "@components/input/buttons/ToggleButtonBox";
import LoadingBox from "@components/input/LoadingBox";
import AttributesInput from './AttributesInput';

interface Props {
  obj: Creature | CreatureGift;
  disabled: boolean;
  onChange: () => void;
}

const api = DandDAPI.getInstance();

export default function CreatureAttributes({
  obj,
  disabled,
  onChange
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [attributes_cr, setAttributesCR] = useState(obj.attributes_table.entries.length > 0 ? Math.min(...obj.attributes_table.entries.map(o => o.level)) : -1);
  const [ping, setPing] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [display_mode, setDisplayMode] = useState("Attributes");
  const [add_at_level, setAddAtLevel] = useState("Next");
  const [trickle_up, setTrickleUp] = useState(false);
  const [shift_by_level, setShiftByLevel] = useState(0);
  const [edit_attribute, setEditAttribute] = useState("");
  const [conditions, setConditions] = useState<Condition[]>([]);
  const [tools, setTools] = useState<Tool[]>([]);

  const load = useCallback( async () => {
    setLoading(true);
    const res = await api.getSetOfObjects({ data_types: ["condition","tool"] });
    if (res) {
      setConditions(res.condition as Condition[]);
      setTools(res.tool as Tool[]);
    }
    setLoading(false);
  }, []);
  
  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      load();
    }
  }, [fetched, load]);
  
  const useRowStyles = makeStyles()((theme) => {
    return ({
      root: {
        backgroundColor: theme.palette.secondary.main,
        color: "white"
      },
    });
  });

  useEffect(() => {
    if(reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);
  
  const { classes: rowClasses, cx: cx2 } = useRowStyles();
  
  const cr_options = obj instanceof Creature ? CR_OPTIONS.filter(o => 
    +o >= obj.min_challenge_rating && +o <= obj.max_challenge_rating 
  ) : CHARACTER_LEVELS;
  const used_levels = obj.attributes_table.entries.map(o => o.level);

  const renderTable = () => {
    let level_options: number[] = [];
    if (obj instanceof Creature) {
      level_options = CR_OPTIONS.map(o => +o).filter(o => 
        o >= obj.min_challenge_rating && 
        o <= obj.max_challenge_rating
      );
    } else {
      level_options = CHARACTER_LEVELS.map(o => +o).filter(o => o < obj.max_level);
    }
    const min = level_options.length === 0 ? 0 : Math.min(...level_options);
    const max = level_options.length === 0 ? 0 : Math.max(...level_options);
    const empty_entries = level_options.map(o => +o).filter(o => o > min && o < max);
    return (
      <>
        <Grid
          className="header"
          size={{
            xs: 4
          }}>
          Entries&nbsp;
          <AddButton
            disabled={disabled}
            onClick={() => {
              const new_obj = obj;
              const entry = new Attributes();
              if (add_at_level === "Next") {
                const max_level = Math.max(...new_obj.attributes_table.entries.map(o => o.level), 0);
                entry.level = 1;
                const previous = new_obj.attributes_table.entries.find(o => o.level === max_level);
                if (previous) {
                  if (previous.level > 0.5) { // It's going to be a whole number, so just increment
                    entry.level = previous.level + 1;
                  } else {
                    const level_options = (new_obj instanceof Creature ? [...CREATURE_CRS] : [...CHARACTER_LEVELS]).map(o => +o).filter(o => o > previous.level);
                    entry.level = Math.min(...level_options);
                  }
                  entry.copy(previous, true);
                } 
              } else {
                entry.level = +add_at_level;
                const max_level = Math.max(...new_obj.attributes_table.entries.filter(o => o.level < entry.level).map(o => o.level), 0);
                const previous = new_obj.attributes_table.entries.find(o => o.level === max_level);
                if (previous) {
                  entry.copy(previous, true);
                } else {
                  // It's a new lowest.  Make it match the previous lowest, and remove trickle up.
                  const min_level = new_obj.attributes_table.entries.length === 0 ? 0 : Math.min(...new_obj.attributes_table.entries.map(o => o.level));
                  const lowest = new_obj.attributes_table.entries.find(o => o.level === min_level);
                  if (lowest) {
                    entry.copy(lowest, true);
                    setTrickleUp(false);
                  } 
                }
                setAddAtLevel("Next");
              }
              new_obj.attributes_table.entries.push(entry);
              setPing(!ping);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <SelectStringBox
            name="Add at Level"
            options={level_options.filter(o => !used_levels.includes(o)).map(o => { return `${o}`; })}
            extra_options={["Next"]}
            value={add_at_level}
            disabled={disabled}
            onChange={setAddAtLevel}
          />
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <CheckBox
            name="Trickle Up"
            value={trickle_up}
            disabled={disabled}
            onChange={setTrickleUp}
          />
        </Grid>
        <Grid
          size={{
            xs: 3
          }}>
          <StringBox
            name="Shift by Level"
            value={`${shift_by_level}`}
            disabled={disabled}
            onBlur={(changed: string) => {
              setShiftByLevel(+changed);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 3
          }}>
          <ButtonBox
            name="Shift Up"
            disabled={disabled || shift_by_level === 0}
            onClick={() => {
              const new_obj = obj;
              for (const entry of new_obj.attributes_table.entries) {
                entry.level += shift_by_level;
              }
              onChange();
              setShiftByLevel(0);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 3
          }}>
          <ButtonBox
            name="Shift Down"
            disabled={disabled || shift_by_level === 0}
            onClick={() => {
              const new_obj = obj;
              for (const entry of new_obj.attributes_table.entries) {
                entry.level -= shift_by_level;
              }
              onChange();
              setShiftByLevel(0);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 3
          }}>
          <ButtonBox
            name="Extrapolate Empty Entries"
            disabled={disabled || empty_entries.length === 0}
            onClick={() => {
              const new_obj = obj;
              new_obj.attributes_table.extrapolateEmptyFields();
              onChange();
              setPing(!ping);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <Grid container spacing={0} direction="row"
            className={cx2(rowClasses.root)}>
            <Grid
              className='centered'
              size={{
                xs: 4
              }}>
              Level
            </Grid>
            <Grid
              className='centered'
              size={{
                xs: 4
              }}>
              
            </Grid>
            <Grid
              size={{
                xs: 4
              }}>
            </Grid>
          </Grid>
        </Grid>
        { obj.attributes_table.entries.sort((a, b) => { return a.level - b.level; }).map(entry => {
          let options: string[] = []; 
          if (obj instanceof Creature) {
            options = CR_OPTIONS.map(o => +o).filter(o => 
              o >= obj.min_challenge_rating && 
              o <= obj.max_challenge_rating &&
              (o === entry.level || !used_levels.includes(o))
            ).map(o => { return `${o}`; });
          } else {
            options = CHARACTER_LEVELS.map(o => +o).filter(o => 
              o === entry.level || !used_levels.includes(o)).map(o => { return `${o}`; });
          }
          return (
            <Grid
              key={entry.true_id}
              className="padded"
              size={{
                xs: 12
              }}>
              <Grid container spacing={0} direction="row"
                className={cx2(rowClasses.root)}>
                <Grid
                  size={{
                    xs: 4
                  }}>
                  <SelectStringBox
                    name=""
                    options={options}
                    value={ `${entry.level}` }
                    onChange={(changed: string) => {
                      const new_obj = obj;
                      const new_cr = +changed;
                      const old_entry = new_obj.attributes_table.entries.find(o => o.level === entry.level);
                      if (old_entry) {
                        old_entry.level = new_cr;
                        onChange();
                      }
                      setAttributesCR(new_cr);
                      setReloading(true);
                    }}
                  />
                </Grid>
                <Grid
                  className="clickable centered"
                  onClick={() => {
                    setAttributesCR(entry.level);
                    setDisplayMode("Attributes");
                  }}
                  size={{
                    xs: 4
                  }}>
                  Select
                </Grid>
                <Grid
                  size={{
                    xs: 4
                  }}>
                  <CopyButton
                    onClick={() => {
                      const new_obj = obj;
                      const new_entry = entry.clone();
                      if (add_at_level === "Next") {
                        new_entry.level++;
                      } else {
                        new_entry.level = +add_at_level;
                      }
                      new_obj.attributes_table.entries.push(new_entry);
                      onChange();
                      setReloading(true);
                    }}
                  />
                  &nbsp;
                  <DeleteButton
                    onClick={() => {
                      const new_obj = obj;
                      new_obj.attributes_table.entries = new_obj.attributes_table.entries.filter(o => o.level !== entry.level);
                      onChange();
                      setReloading(true);
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          );
        })}
        <Grid
          size={{
            xs: 12
          }}></Grid>
      </>
    );
  }

  const renderAttributes = () => {
    const use_entry = obj.attributes_table.entries.find(o => o.level === attributes_cr);
    if (use_entry) {
      return (
        <AttributesInput
          attributes={use_entry}
          disabled={disabled}
          onChange={onChange}
        />
      );
    }
    return null;
  }

  if (!fetched || loading) {
    return (<LoadingBox />);
  } else {
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          size={{
            xs: 12
          }}>
          <CenteredMenu
            options={["Attributes","Table"]}
            selected={display_mode}
            onSelect={setDisplayMode}
          />
        </Grid>
        { display_mode === "Attributes" ? 
          <>
            <Grid container spacing={0} direction="row">
              <Grid
                size={{
                  xs: 12
                }}>
                <SelectStringBox 
                  name="Select CR for Attributes"
                  options={cr_options}
                  value={`${attributes_cr}`}
                  onChange={(changed: string) => {
                    const new_obj = obj;
                    const new_cr = +changed;
                    if (new_obj.attributes_table.entries.length === 0) {
                      const entry = new Attributes();
                      entry.level = new_cr;
                      new_obj.attributes_table.entries.push(entry);
                    } else {
                      const old_entry = new_obj.attributes_table.entries.find(o => o.level === new_cr);
                      if (!old_entry && new_obj.attributes_table.entries.length > 0) {
                        const crs = new_obj.attributes_table.entries.map(o => o.level);
                        let copy_cr = Math.max(...crs.filter(o => o < new_cr), -1);
                        if (copy_cr === -1) {
                          copy_cr = Math.min(...crs.filter(o => o > new_cr), -1);
                        }
                        const entry = copy_cr === -1 ? new Attributes() : new_obj.attributes_table.entries.filter(o => o.level === copy_cr)[0].clone();
                        entry.level = new_cr;
                        new_obj.attributes_table.entries.push(entry);
                      }
                    }
                    onChange();
                    setAttributesCR(new_cr);
                    setReloading(true);
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12
                }}>{ attributes_cr > -1 && renderAttributes() }</Grid>
            </Grid>
          </>
        : renderTable() }
      </Grid>
    ); 
  }
}
